import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, ArrowRight, ShieldCheck } from "lucide-react";
import { COMPANY, IMAGES, SERVICE_AREAS } from "@/data";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1.0, delay: 0.25 + i * 0.14, ease: [0.76, 0, 0.24, 1] },
  }),
};

const H1_LINES = ["Remodeling & Construction", "in Charlotte, Pineville", "& Matthews, NC"];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yBg = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scaleBg = useTransform(scrollYProgress, [0, 1], [1.05, 1.2]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen flex items-end overflow-hidden bg-navy-dark"
    >
      <motion.div className="absolute inset-0 z-0" style={{ y: yBg, scale: scaleBg }}>
        <img
          src={IMAGES.hero}
          alt="Open-concept kitchen remodel with quartz island in Charlotte NC"
          className="w-full h-full object-cover"
          width="1600"
          height="1067"
          fetchPriority="high"
        />
      </motion.div>
      <div className="absolute inset-0 z-[1] bg-navy/70" />
      <div className="absolute inset-0 z-[1] bg-gradient-to-t from-navy-dark via-navy-dark/30 to-transparent" />

      {/* Rotating "Free Estimate" seal */}
      <div className="absolute z-10 top-24 right-6 md:top-28 md:right-12 hidden sm:block" data-testid="hero-seal">
        <div className="relative w-28 h-28 md:w-36 md:h-36">
          <motion.div
            className="absolute inset-0 rounded-full border-2 border-dashed border-gold"
            animate={{ rotate: 360 }}
            transition={{ duration: 22, repeat: Infinity, ease: "linear" }}
          />
          <div className="absolute inset-[8px] rounded-full bg-gold flex flex-col items-center justify-center text-center">
            <span className="font-body text-[9px] md:text-[10px] font-bold uppercase tracking-[0.15em] text-navy-dark/70">Free</span>
            <span className="font-display font-bold uppercase text-navy-dark text-lg md:text-2xl leading-none">Estimate</span>
            <span className="font-body text-[8px] md:text-[9px] font-bold uppercase tracking-[0.12em] text-navy-dark/70 mt-0.5">No Obligation</span>
          </div>
        </div>
      </div>

      <div className="relative z-10 max-w-[1440px] mx-auto px-5 md:px-12 w-full pb-16 md:pb-24 pt-32">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="inline-flex items-center gap-2 rounded-full bg-gold/15 border border-gold/40 px-4 py-2 mb-6"
          data-testid="hero-badge"
        >
          <ShieldCheck size={16} className="text-gold" />
          <span className="text-gold font-body text-xs md:text-sm uppercase tracking-[0.2em] font-bold">
            Free Estimate — No Obligation
          </span>
        </motion.div>

        {/* Eyebrow (styled, NOT an h1) */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="font-display uppercase tracking-[0.18em] text-gold text-base md:text-xl mb-3"
          data-testid="hero-eyebrow"
        >
          More than building, we transform.
        </motion.p>

        <h1 className="font-display font-bold uppercase text-white leading-[0.9] tracking-tight text-4xl sm:text-5xl md:text-6xl lg:text-7xl max-w-[18ch]">
          {H1_LINES.map((l, i) => (
            <span key={l} className="block overflow-hidden">
              <motion.span custom={i} variants={line} initial="hidden" animate="show" className="inline-block">
                {i === 2 ? (
                  <>
                    &amp; Matthews, <span className="text-gold">NC</span>
                  </>
                ) : (
                  l
                )}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.0 }}
          className="mt-6 max-w-2xl font-body text-white/80 text-base md:text-lg leading-relaxed"
          data-testid="hero-service-area"
        >
          Family-owned remodeling & construction for homeowners and businesses. Serving North &amp; South Carolina:{" "}
          {SERVICE_AREAS.map((a, i) => (
            <span key={a.slug}>
              <Link to={`/service-areas/${a.slug}`} className="text-gold hover:underline font-medium">
                {a.city}
              </Link>
              {i < SERVICE_AREAS.length - 1 ? ", " : ""}
            </span>
          ))}{" "}
          & the greater Charlotte region.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.15 }}
          className="mt-8 flex flex-col sm:flex-row gap-4"
        >
          <a
            href="#contact"
            data-testid="hero-estimate-btn"
            className="group inline-flex items-center justify-center gap-2 rounded-full bg-gold hover:bg-gold-hover text-navy-dark font-bold uppercase tracking-wide text-sm px-8 py-4 transition-colors duration-200"
          >
            Get Free Estimate
            <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform duration-200" />
          </a>
          <a
            href={`tel:${COMPANY.phoneRaw}`}
            data-testid="hero-call-btn"
            className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-white/40 text-white hover:border-gold hover:text-gold font-semibold text-sm px-8 py-4 transition-colors duration-200"
          >
            <Phone size={18} /> Call {COMPANY.phone}
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
